import type { JournalEntry, ATASpec, Project, Surcharge } from '../../lib/api/types'
import { ataNettoTotal, formatSEK, formatPercent } from '../../utils/calculations'

interface Props {
  journal: JournalEntry | null
  ataSpec: ATASpec
  project: Project | null
  surcharges: Surcharge[]
  sortOrder: number
}

/** The A4 "ÄTA-specifikation" sheet, extracted from ATASpecPrint for reuse in
 *  the combined print (#12356). */
export default function ATASpecPaper({ journal, ataSpec, project, surcharges, sortOrder }: Props) {
  const nr = `ÄTA-${String(sortOrder).padStart(3, '0')}`
  const proj: Partial<Project> = project ?? {}
  const lineItems = ataSpec.lineItems ?? []

  const subtotal = lineItems.reduce((s, item) => s + (item.mangd ?? 0) * (item.prisPerEnhet ?? 0), 0)
  const activeSC = surcharges.filter(s => s.procent > 0 && s.typ)
  const totalExklMoms = ataNettoTotal(lineItems, surcharges)

  return (
    <div className="paper">
      {/* Letterhead */}
      <div className="paper-letterhead">
        <div>
          <div className="paper-sender-name">TIBRAM BYGG AB</div>
          <div className="paper-sender-addr">
            {proj.adress && <>{proj.adress}<br /></>}
            {proj.postadress ?? ''}
          </div>
        </div>
        <div>
          <div className="paper-doc-type">ÄTA-specifikation</div>
          <div className="paper-doc-ref">{nr}</div>
        </div>
      </div>

      {/* 4-col meta */}
      <div className="paper-meta-grid">
        <div>
          <div className="paper-meta-key">Projekt</div>
          <div className="paper-meta-val">{proj.projektnr ?? '–'}</div>
        </div>
        <div>
          <div className="paper-meta-key">Beställare</div>
          <div style={{ fontSize: '10pt' }}>{proj.bestellare ?? '–'}</div>
        </div>
        <div>
          <div className="paper-meta-key">Datum</div>
          <div className="paper-meta-val">{ataSpec.datum ?? new Date().toISOString().slice(0,10)}</div>
        </div>
        <div>
          <div className="paper-meta-key">Vår ref</div>
          <div style={{ fontSize: '10pt' }}>{proj.platschef ?? '–'}</div>
        </div>
      </div>

      <hr className="paper-divider" />

      {/* Avseende */}
      <div style={{ marginBottom: '12pt' }}>
        <div style={{ fontSize: '8.5pt', textTransform: 'uppercase', letterSpacing: '.06em', color: '#888', marginBottom: '4pt' }}>Avseende</div>
        <div className="paper-avseende-title">{journal?.arbeteBeskrivning ?? nr}</div>
        {journal?.arbeteDetaljer && (
          <div className="paper-avseende-desc" style={{ marginTop: '4pt', whiteSpace: 'pre-wrap' }}>{journal.arbeteDetaljer}</div>
        )}
      </div>

      {/* Spec table */}
      <table>
        <thead>
          <tr>
            <th style={{ width: '60pt' }}>Kod</th>
            <th>Benämning</th>
            <th className="r" style={{ width: '45pt' }}>Mängd</th>
            <th style={{ width: '35pt' }}>Enh</th>
            <th className="r" style={{ width: '65pt' }}>À-pris</th>
            <th className="r" style={{ width: '75pt' }}>Summa</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '12pt 0' }}>Inga rader</td>
            </tr>
          )}
          {lineItems.map((item, i) => {
            const rad = (item.mangd ?? 0) * (item.prisPerEnhet ?? 0)
            return (
              <tr key={i}>
                <td className="code">{item.kod ?? ''}</td>
                <td>{item.specifikation ?? ''}</td>
                <td className="r">{item.mangd != null ? item.mangd.toLocaleString('sv-SE') : '–'}</td>
                <td>{item.enhet ?? ''}</td>
                <td className="r">{item.prisPerEnhet != null ? item.prisPerEnhet.toLocaleString('sv-SE') : '–'}</td>
                <td className="r">{formatSEK(rad)}</td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} style={{ textAlign: 'right', color: '#555', paddingTop: '8pt' }}>Summa rader</td>
            <td className="r" style={{ paddingTop: '8pt', fontFamily: 'monospace' }}>{formatSEK(subtotal)}</td>
          </tr>
          {activeSC.map(s => (
            <tr key={s.sortOrder} className="dim">
              <td colSpan={5} style={{ textAlign: 'right', color: '#888' }}>{s.typ} ({formatPercent(s.procent)})</td>
              <td className="r" style={{ fontFamily: 'monospace', color: '#888' }}>
                {formatSEK(subtotal * s.procent)}
              </td>
            </tr>
          ))}
          <tr className="sum-line">
            <td colSpan={5} style={{ textAlign: 'right' }}>Total exkl. moms</td>
            <td className="r">{formatSEK(totalExklMoms)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="paper-villkor">
        Prisuppgifter är exklusive mervärdesskatt. Betalningsvillkor:{' '}
        {proj.betalningsvillkor ?? '30 dagar netto'}.{' '}
        Dröjsmålsränta enligt lag.
      </div>

      {/* Signature */}
      <div className="paper-signature">
        <div>
          <div className="paper-sig-line" />
          <div className="paper-sig-label">
            {proj.platschef ?? 'Platschef'}<br />Platschef, Tibram Bygg AB
          </div>
        </div>
        <div>
          <div className="paper-sig-line" />
          <div className="paper-sig-label">Datum och underskrift, beställare</div>
        </div>
      </div>

      <div className="paper-footer">
        {nr} · {proj.projektnr ?? '–'} &nbsp;—&nbsp; Sida 1 (1)
      </div>
    </div>
  )
}
